import React, { useState, useEffect, useRef } from 'react';
import { Download, Eye, Calendar, MapPin, Plus, Play, X, Trash2, Pencil, Upload, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../services/api';

const API = `${API_BASE_URL}/api`;

const Gallery = () => {
  const { user, token } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [mediaItems, setMediaItems] = useState([]);
  const [categories, setCategories] = useState(['Tous']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Lightbox state (image/video player)
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Quick upload refs
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const isFounder = user && user.user_type === 'fondateur';

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = { title: '', description: '', category: '', media_type: 'gallery_image', image_url: '', video_url: '', status: 'published' };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadGalleryItems();
    loadCategories();
  }, []);

  const loadGalleryItems = async () => {
    setLoading(true);
    setError('');
    try {
      const types = ['gallery_image', 'photo', 'video', 'documentary'];
      const all = [];
      for (const t of types) {
        try {
          const res = await axios.get(`${API}/media/items`, { params: { media_type: t, limit: 50 } });
          const data = res?.data;
          const arr = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
          all.push(...arr);
        } catch (err1) {
          try {
            const res2 = await axios.get(`/api/media/items`, { params: { media_type: t, limit: 50 } });
            const d2 = res2?.data;
            const arr2 = Array.isArray(d2) ? d2 : (Array.isArray(d2?.items) ? d2.items : []);
            all.push(...arr2);
          } catch (err2) {
            console.error(`Erreur chargement type ${t}:`, err2);
          }
        }
      }
      const map = new Map();
      all.forEach(it => { if (it && it.id && !map.has(it.id)) map.set(it.id, it); });
      setMediaItems(Array.from(map.values()));
    } catch (error) {
      console.error('Erreur lors du chargement de la galerie:', error);
      setError('Erreur lors du chargement de la galerie');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/media/categories`, { withCredentials: true });
      const categoryNames = response.data.map(cat => cat.name);
      setCategories(['Tous', ...categoryNames]);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    }
  };

  const filteredItems = selectedCategory === 'Tous' 
    ? mediaItems 
    : mediaItems.filter(item => item.category === selectedCategory);

  const getYouTubeId = (url) => {
    if (!url) return '';
    try {
      if (url.includes('/embed/')) return url.split('/embed/')[1].split(/[?&]/)[0];
      if (url.includes('youtu.be/')) return url.split('youtu.be/')[1].split(/[?&]/)[0];
      if (url.includes('watch?v=')) return new URL(url).searchParams.get('v') || '';
      if (url.includes('/shorts/')) return url.split('/shorts/')[1].split(/[?&]/)[0];
    } catch { return ''; }
    return '';
  };
  const isYouTube = (url) => !!getYouTubeId(url);
  const ytThumb = (url) => { const id = getYouTubeId(url); return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null; };

  const isVideoItem = (item) => item.media_type === 'video' || (!!item.video_url && !item.image_url);

  const renderMediaThumb = (item) => {
    const isVideo = isVideoItem(item);
    const src = item.image_url || (item.metadata?.thumbnail_url) || (isVideo ? ytThumb(item.video_url) : null) || null;
    if (!src) {
      return (
        <div className="relative aspect-video flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10">
          {isVideo ? (
            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
              <Play className="h-6 w-6 text-primary ml-1" />
            </div>
          ) : (
            <span className="text-muted-foreground text-sm">Aperçu</span>
          )}
        </div>
      );
    }
    return (
      <div className="relative aspect-video overflow-hidden group">
        <img 
          src={src}
          alt={item.title}
          className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
        />
        <div className="pointer-events-none absolute inset-0 transition-opacity duration-500 group-hover:bg-black/25 bg-black/10" />
        {isVideo && (
          <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
              <Play className="h-6 w-6 text-primary ml-1" />
            </div>
          </div>
        )}
      </div>
    );
  };

  const openViewer = (item) => {
    setCurrentItem(item);
    setViewerOpen(true);
    document.body.style.overflow = 'hidden';
  };
  const closeViewer = () => {
    setViewerOpen(false);
    setCurrentItem(null);
    document.body.style.overflow = '';
  };

  const onCardClick = (item) => openViewer(item);

  // Quick upload helpers (admin only)
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await axios.post(`${API}/media/upload`, fd, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined
      }
    });
    return res.data; // { success, url, filename, original_name }
  };

  const createMediaItem = async (payload) => {
    await axios.post(`${API}/media/items`, payload, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
        'Content-Type': 'application/json'
      }
    });
  };

  const handleQuickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const up = await uploadFile(file);
      if (up?.url) {
        await createMediaItem({
          title: file.name,
          description: '',
          media_type: 'gallery_image',
          status: 'published',
          image_url: up.url,
          video_url: null,
          document_url: null,
          tags: ['galerie'],
          category: 'Équipe',
          featured: false,
          price: null, currency: 'EUR', in_stock: true, stock_quantity: null,
          content: '', excerpt: ''
        });
        await loadGalleryItems();
      }
    } catch (err) {
      alert('Erreur upload photo: ' + (err?.response?.data?.detail || err.message));
    } finally {
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleQuickVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const up = await uploadFile(file);
      if (up?.url) {
        await createMediaItem({
          title: file.name,
          description: '',
          media_type: 'video',
          status: 'published',
          image_url: null,
          video_url: up.url,
          document_url: null,
          tags: ['galerie'],
          category: 'Équipe',
          featured: false,
          price: null, currency: 'EUR', in_stock: true, stock_quantity: null,
          content: '', excerpt: ''
        });
        await loadGalleryItems();
      }
    } catch (err) {
      alert('Erreur upload vidéo: ' + (err?.response?.data?.detail || err.message));
    } finally {
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const deleteMediaItem = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement "${item.title || 'ce média'}" ?`)) return;
    try {
      await axios.delete(`${API}/media/items/${item.id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      });
      await loadGalleryItems();
    } catch (err) {
      alert('Erreur suppression: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (e, item) => {
    e.stopPropagation();
    setEditingId(item.id);
    setForm({
      title: item.title || '', description: item.description || '', category: item.category || '',
      media_type: item.media_type || 'gallery_image', image_url: item.image_url || '',
      video_url: item.video_url || '', status: item.status || 'published',
    });
    setModalOpen(true);
  };
  const handleModalFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const up = await uploadFile(file);
      if (up?.url) setForm(f => f.media_type === 'video' ? { ...f, video_url: up.url } : { ...f, image_url: up.url });
    } catch (err) { alert('Erreur upload: ' + (err?.response?.data?.detail || err.message)); }
    finally { setUploading(false); }
  };
  const handleModalSave = async () => {
    if (!form.title.trim()) { alert('Le titre est obligatoire'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description, media_type: form.media_type, status: form.status,
        image_url: form.image_url || null, video_url: form.video_url || null, category: form.category || null,
      };
      if (editingId) {
        await axios.put(`${API}/media/items/${editingId}`, payload, { headers: { Authorization: token ? `Bearer ${token}` : undefined, 'Content-Type': 'application/json' } });
      } else {
        await createMediaItem(payload);
      }
      setModalOpen(false); setEditingId(null); setForm(emptyForm);
      await loadGalleryItems(); await loadCategories();
    } catch (err) { alert('Erreur enregistrement: ' + (err?.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen relative">
      <div className="mesh-bg" />
      <div className="grid-overlay" />
      <div className="container mx-auto px-4 py-8 relative">
        <div className="text-center mb-12">
          <div className="flex justify-between items-center mb-6">
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-4">Galerie Photo & Vidéo</h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Découvrez en images nos actions sur le terrain, nos projets en cours et l'impact de votre soutien.
              </p>
            </div>
            {isFounder && (
              <div className="flex flex-col gap-2 ml-6 text-left">
                <div className="flex gap-2">
                  <Button onClick={openCreate} className="flex items-center gap-2" data-testid="gallery-add-btn"><Plus className="h-4 w-4" />Ajouter un média</Button>
                </div>
                {/* Ajout rapide par upload direct */}
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <label className="text-sm text-muted-foreground">Upload rapide photo
                    <input ref={photoInputRef} type="file" accept="image/*" className="mt-1 block w-full text-sm" onChange={handleQuickPhoto} />
                  </label>
                  <label className="text-sm text-muted-foreground">Upload rapide vidéo (mp4)
                    <input ref={videoInputRef} type="file" accept="video/mp4,video/*" className="mt-1 block w-full text-sm" onChange={handleQuickVideo} />
                  </label>
                </div>
                <div className="text-xs text-muted-foreground">Astuce: les fichiers uploadés sont automatiquement publiés dans la galerie.</div>
              </div>
            )}
          </div>
        </div>

        {error && (<Alert variant="destructive" className="mb-6"><AlertDescription>{error}</AlertDescription></Alert>)}

        {categories.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories.map((category) => (
              <Button key={category} variant={selectedCategory === category ? 'default' : 'outline'} onClick={() => setSelectedCategory(category)} className="mb-2">{category}</Button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement de la galerie...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-2xl font-bold mb-4">Aucun média disponible</h3>
            <p className="text-muted-foreground mb-6">{isFounder ? 'Commencez par ajouter vos premières photos et vidéos.' : 'Revenez bientôt pour de nouveaux médias.'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => (
              <Card key={item.id} className="group glow-border overflow-hidden hover:shadow-lg transition-all duration-300 border-border/50 hover:border-primary/20 cursor-pointer relative" onClick={() => onCardClick(item)}>
                {isFounder && (
                  <div className="absolute top-2 right-2 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => openEdit(e, item)}
                      className="p-2 rounded-full bg-background/80 backdrop-blur border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                      title="Modifier ce média" aria-label="Modifier ce média" data-testid="gallery-edit-btn"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => deleteMediaItem(e, item)}
                      className="p-2 rounded-full bg-background/80 backdrop-blur border border-border text-muted-foreground hover:text-destructive hover:border-destructive transition-colors"
                      title="Supprimer ce média" aria-label="Supprimer ce média" data-testid="gallery-delete-btn"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {renderMediaThumb(item)}
                <div className="relative -mt-0">
                  {/* Overlay texte centré */}
                  <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
                    <div className="relative z-10 text-white text-center max-w-[85%] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <h3 className="font-semibold mb-2 drop-shadow-md">{item.title}</h3>
                      <p className="text-white/90 text-xs mb-3 line-clamp-3">{item.description}</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-4 hidden"></CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal Ajouter / Modifier un média */}
      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4" data-testid="gallery-editor-modal">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg my-8 shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-bold">{editingId ? 'Modifier le média' : 'Ajouter un média'}</h2>
              <button onClick={() => setModalOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Titre *</label>
                <input className="w-full rounded-md border border-border bg-background px-3 py-2" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Titre du média" data-testid="gallery-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select className="w-full rounded-md border border-border bg-background px-3 py-2" value={form.media_type} onChange={e => setForm({ ...form, media_type: e.target.value })} data-testid="gallery-type-select">
                    <option value="gallery_image">Photo</option>
                    <option value="video">Vidéo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Catégorie</label>
                  <input className="w-full rounded-md border border-border bg-background px-3 py-2" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="ex: Actions terrain" data-testid="gallery-category-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{form.media_type === 'video' ? 'Fichier vidéo (mp4)' : 'Fichier image'}</label>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border cursor-pointer hover:bg-primary/5 text-sm">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Choisir un fichier
                    <input type="file" accept={form.media_type === 'video' ? 'video/*' : 'image/*'} className="hidden" onChange={handleModalFile} data-testid="gallery-file-input" />
                  </label>
                  {form.image_url && <img src={form.image_url} alt="aperçu" className="h-12 w-20 object-cover rounded" />}
                  {form.video_url && <span className="text-xs text-green-500">Vidéo prête ✓</span>}
                </div>
              </div>
              {form.media_type === 'video' ? (
                <div>
                  <label className="block text-sm font-medium mb-1">Lien vidéo (YouTube recommandé)</label>
                  <input
                    className="w-full rounded-md border border-border bg-background px-3 py-2"
                    value={form.video_url || ''}
                    onChange={e => setForm({ ...form, video_url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                    data-testid="gallery-video-url-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Collez simplement un lien YouTube — aucun fichier à téléverser, aucune limite de taille.</p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-1">URL de l'image (optionnel)</label>
                  <input
                    className="w-full rounded-md border border-border bg-background px-3 py-2"
                    value={form.image_url || ''}
                    onChange={e => setForm({ ...form, image_url: e.target.value })}
                    placeholder="https://... (ou utilisez le bouton ci-dessus)"
                    data-testid="gallery-image-url-input"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea className="w-full rounded-md border border-border bg-background px-3 py-2" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description courte" data-testid="gallery-description-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Statut</label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="gallery-status-select">
                  <option value="published">Publié (visible par tous)</option>
                  <option value="draft">Brouillon (admin seulement)</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <Button variant="ghost" onClick={() => setModalOpen(false)}>Annuler</Button>
              <Button onClick={handleModalSave} disabled={saving || uploading} data-testid="gallery-save-btn">{saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Viewer modal image/vidéo */}
      {viewerOpen && currentItem && (
        <div className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-center justify-center" onClick={closeViewer}>
          <button type="button" onClick={(e) => { e.stopPropagation(); closeViewer(); }} className="absolute top-4 right-4 text-white/80 hover:text-white text-sm border border-white/20 rounded px-3 py-1 flex items-center gap-1"><X className="h-4 w-4"/>Fermer</button>
          <div className="max-w-[92vw] max-h-[82vh] p-2" onClick={(e) => e.stopPropagation()}>
            {isVideoItem(currentItem) ? (
              isYouTube(currentItem.video_url) ? (
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(currentItem.video_url)}?autoplay=1`}
                  title={currentItem.title}
                  className="w-[92vw] max-w-4xl aspect-video rounded shadow-lg"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video src={currentItem.video_url} controls autoPlay className="max-w-[92vw] max-h-[82vh] w-auto h-auto rounded shadow-lg" />
              )
            ) : (
              <img src={currentItem.image_url} alt={currentItem.title} className="max-w-[92vw] max-h-[82vh] object-contain rounded shadow-lg" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;